/**
 * patient360Context.ts — Stable context module for Patient 360 workspace.
 *
 * IMPORTANT: This file must stay separate from PatientWorkspaceLayout.tsx.
 * Keeping createContext() here prevents Vite HMR from recreating the context
 * object when PatientWorkspaceLayout is re-evaluated, which would break the
 * Provider/consumer contract and throw "usePatient360Ctx must be used inside
 * PatientWorkspaceLayout".
 */

import { createContext, useContext } from 'react';

// ─── Context value type ───────────────────────────────────────
// Complex hook return types are inlined here as `any` to avoid
// circular import chains; PatientWorkspaceLayout casts to these.

export interface Patient360OutletContext {
  programId: string;
  patientId: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  vm: any;
  data: {
    patient: any;
    financial: any;
    summary: any;
    cases: any[];
    cycleTimeline: any[];
    unifiedTimeline: any[];
    documents: any[];
    workflow: any[];
    calls: any[];
    notifications: any[];
    adverseEvents: any[];
    claims: any[];
  };
  permissions: {
    visibleKeys: string[];
    canViewFinancialAmounts: boolean;
    canDownloadDocuments: boolean;
    canCreateCase: boolean;
    canAccessCases: boolean;
    canApproveCycles: boolean;
  };
  financialVisibility: {
    hideMonetaryTrackingFields: boolean;
    showMonetaryTrackingFields: boolean;
    showFinancialKpis: boolean;
    showFinancialSnapshot: boolean;
    showCaseFinancialAmounts: boolean;
    showMonetaryProgressIndicators: boolean;
    showMonetaryWarnings: boolean;
    showFinancialSummaryValues: boolean;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patientEntity: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  alertState: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  needsAttentionItems: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeCase: any;
  activeCycleId: string | null;
  hasActiveCase: boolean;
  hasActiveCycle: boolean;
  isRefreshing: boolean;
  refetchPatient360: () => void;
  handleEditPatient: () => void;
  handleCreateCase: () => void;
  handleAddUtilization: (caseId: string, planType: string, caseName?: string) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleCycleApprovalAction: (payload: any) => Promise<void>;
  handleViewCycle: () => void;
  handleRequestTopUp: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleNavChange: (key: any, entityId?: string) => void;
  userRole: string | undefined;
  cycleActionState: { enabled: boolean; reason?: string };
  utilizedAmount: number;
  utilizationPct: number;
}

// ─── Context ──────────────────────────────────────────────────

export const Patient360Ctx = createContext<Patient360OutletContext | null>(null);

// ─── Hook ─────────────────────────────────────────────────────

export function usePatient360Ctx(): Patient360OutletContext {
  const ctx = useContext(Patient360Ctx);
  if (!ctx) throw new Error('usePatient360Ctx must be used inside PatientWorkspaceLayout');
  return ctx;
}
